import { canonicalMetricId } from './asset-observability';
import { customerFinding, customerMetricLabel } from '@aot-aiops/shared';

export type AssetSection = {
  name: string;
  tabs: string[];
};

export type EvidenceWindow = {
  range: string;
  from?: string;
  to?: string;
};

export type CoverageItem = {
  id: string;
  label: string;
  state: 'AVAILABLE' | 'NOT_CONFIGURED' | 'NOT_REPORTED' | 'NOT_APPLICABLE';
  guidance?: string;
  action?: 'integrations' | 'configuration' | 'services';
  counted?: boolean;
};

const tabSections: Record<string, string> = {
  Overview: 'Overview',
  Metrics: 'Performance',
  Network: 'Performance',
  Storage: 'Performance',
  Interfaces: 'Performance',
  Hardware: 'Performance',
  'Virtual Machines': 'Performance',
  Logs: 'Logs & Events',
  Events: 'Logs & Events',
  History: 'Logs & Events',
  Topology: 'Dependencies',
  Map: 'Dependencies',
  Relationships: 'Dependencies',
  Services: 'Dependencies',
  Behavior: 'Intelligence',
  Incidents: 'Intelligence',
  Capacity: 'Intelligence',
  Investigate: 'Intelligence',
  OpsAI: 'Intelligence',
  Configuration: 'Configuration',
};

export function assetSectionForTab(tab: string) {
  return tabSections[tab] ?? 'Overview';
}

export function assetNavigation(input: {
  hasNetwork: boolean;
  hasStorage: boolean;
  hasInterfaces: boolean;
  isHypervisor: boolean;
  hasHardware: boolean;
  hasServices: boolean;
}): AssetSection[] {
  const performance = [
    'Metrics',
    ...(input.hasNetwork ? ['Network'] : []),
    ...(input.hasStorage ? ['Storage'] : []),
    ...(input.hasInterfaces ? ['Interfaces'] : []),
    ...(input.hasHardware ? ['Hardware'] : []),
    ...(input.isHypervisor ? ['Virtual Machines'] : []),
  ];
  return [
    { name: 'Overview', tabs: ['Overview'] },
    { name: 'Performance', tabs: performance },
    { name: 'Logs & Events', tabs: ['Logs', 'Events', 'History'] },
    {
      name: 'Dependencies',
      tabs: ['Topology', 'Map', 'Relationships', ...(input.hasServices ? ['Services'] : [])],
    },
    { name: 'Intelligence', tabs: ['Behavior', 'Incidents', 'Capacity', 'Investigate', 'OpsAI'] },
    { name: 'Configuration', tabs: ['Configuration'] },
  ].filter((section) => section.tabs.length > 0);
}

export function evidenceWindowHours(window: EvidenceWindow) {
  if (window.range === 'custom') {
    const from = Date.parse(String(window.from ?? ''));
    const to = Date.parse(String(window.to ?? ''));
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
    return Math.min(720, (to - from) / 3_600_000);
  }
  return ({ '1h': 1, '6h': 6, '24h': 24, '7d': 168, '30d': 720 } as Record<string, number>)[window.range] ?? 24;
}

export function evidenceWindowBounds(window: EvidenceWindow, now = new Date()) {
  if (window.range === 'custom' && window.from && window.to)
    return { from: Date.parse(window.from), to: Date.parse(window.to) };
  const hours = evidenceWindowHours(window) ?? 24;
  return { from: now.getTime() - hours * 3_600_000, to: now.getTime() };
}

export function inEvidenceWindow(value: string | null | undefined, window: EvidenceWindow, now = new Date()) {
  if (!value) return false;
  const at = Date.parse(value);
  const bounds = evidenceWindowBounds(window, now);
  return Number.isFinite(at) && at >= bounds.from && at <= bounds.to;
}

export function goldenMetricIds(assetType: string) {
  if (/switch|router|network-device/.test(assetType))
    return ['cpu.percent', 'memory.percent', 'interface.primary.utilization.percent', 'network.error.percent', 'temperature.celsius'];
  if (/ups/.test(assetType))
    return ['load.percent', 'battery.percent', 'runtime.seconds', 'power.input.watts', 'power.output.watts', 'temperature.celsius'];
  if (/vm|server|hyperv|proxmox|vmware/.test(assetType))
    return ['cpu.percent', 'memory.percent', 'network.receive.bits_per_second', 'network.transmit.bits_per_second', 'disk.write.bytes_per_second'];
  return ['cpu.percent', 'memory.percent', 'network.receive.bits_per_second', 'storage.percent'];
}

function capabilityState(value?: string): CoverageItem['state'] {
  if (['AVAILABLE', 'SUPPORTED', 'SUPPORTED_BY_NATIVE_API', 'SUPPORTED_VIA_GUEST_AGENT', 'COLLECTED'].includes(String(value))) return 'AVAILABLE';
  if (value === 'NOT_CONFIGURED') return 'NOT_CONFIGURED';
  if (['UNSUPPORTED', 'NOT_APPLICABLE'].includes(String(value))) return 'NOT_APPLICABLE';
  return 'NOT_REPORTED';
}

export function monitoringCoverage(input: {
  assetType: string;
  capabilities?: any;
  hasEvents: boolean;
  hasBehavior: boolean;
  hasServices: boolean;
  metadata?: Record<string, unknown>;
}): { items: CoverageItem[]; available: number; applicable: number; summary: string } {
  const metric = (id: string) => input.capabilities?.metrics?.find((item: any) => canonicalMetricId(String(item.id)) === id);
  const area = (id: string) => input.capabilities?.areas?.[id];
  const vm = /(?:proxmox|vmware|hyperv)-(?:vm|lxc)/.test(input.assetType);
  const items: CoverageItem[] = [
    { id: 'cpu', label: 'CPU', state: capabilityState(metric('cpu.percent')?.state) },
    { id: 'memory', label: 'Memory', state: capabilityState(metric('memory.percent')?.state) },
    { id: 'network', label: 'Network', state: capabilityState(area('network')?.state) },
    { id: 'disk-io', label: 'Disk I/O', state: capabilityState(area('storage')?.state) },
    { id: 'events', label: 'Events', state: input.hasEvents ? 'AVAILABLE' : capabilityState(input.capabilities?.events?.state) },
    { id: 'behavior', label: 'Behavior', state: input.hasBehavior ? 'AVAILABLE' : 'NOT_REPORTED' },
    {
      id: 'logs',
      label: 'Logs',
      state: capabilityState(input.capabilities?.logs?.state),
      guidance: input.capabilities?.logs?.guidance ?? 'Configure a log source for this workspace.',
      action: 'integrations',
    },
    {
      id: 'guest-filesystem',
      label: 'Guest filesystem',
      state: vm ? capabilityState(metric('guest.filesystem')?.state) : 'NOT_APPLICABLE',
      guidance: vm ? metric('guest.filesystem')?.guidance : 'Guest filesystem telemetry does not apply to this asset type.',
      action: vm ? 'configuration' : undefined,
    },
    {
      id: 'temperature',
      label: 'Temperature',
      state: vm ? 'NOT_APPLICABLE' : capabilityState(area('environment')?.state),
      guidance: vm ? 'Physical hardware sensors are not applicable to a virtual machine.' : 'Configure supported hardware or environmental telemetry.',
      action: vm ? undefined : 'integrations',
    },
    {
      id: 'services',
      label: 'Business service relationship',
      state: input.hasServices ? 'AVAILABLE' : 'NOT_CONFIGURED',
      guidance: 'Relate the asset to the business service it supports.',
      action: 'services',
      counted: false,
    },
  ];
  const applicable = items.filter((item) => item.counted !== false && item.state !== 'NOT_APPLICABLE').length;
  const available = items.filter((item) => item.counted !== false && item.state === 'AVAILABLE').length;
  return { items, available, applicable, summary: `${available} of ${applicable} applicable capabilities monitored` };
}

export type InvestigationHypothesis = {
  title: string;
  status: 'SUPPORTED' | 'POSSIBLE' | 'DISFAVORED' | 'INSUFFICIENT_EVIDENCE';
  confidence: 'high' | 'medium' | 'low';
  supporting: string[];
  contradicting: string[];
};

export function buildInvestigation(input: any) {
  const findings = (input.findings ?? []).map((item: any) => customerFinding(item, input.asset?.displayName));
  const events = input.events ?? [];
  const metrics = input.metrics ?? [];
  const services = input.services ?? [];
  const active = findings.filter((item: any) => item.state !== 'recovered');
  const network = active.find((item: any) => canonicalMetricId(String(item.metric)) === 'network.transmit.bits_per_second');
  const disk = active.find((item: any) => canonicalMetricId(String(item.metric)) === 'disk.write.bytes_per_second');
  const backup = events.find((item: any) => /backup|vzdump/i.test(`${item.category} ${item.summary}`));
  const cpu = metrics.find((item: any) => canonicalMetricId(String(item.name)) === 'cpu.percent');
  const cpuBaseline = (input.baselines ?? []).find((item: any) => canonicalMetricId(String(item.metric)) === 'cpu.percent' && item.bucket === 'all');
  const cpuNormal = cpu && cpuBaseline && Number(cpu.value) >= Number(cpuBaseline.lowerBound) && Number(cpu.value) <= Number(cpuBaseline.upperBound);
  const hypotheses: InvestigationHypothesis[] = [];
  hypotheses.push({
    title: 'Scheduled backup activity is consistent with the network and disk changes.',
    status: network && disk && backup ? 'SUPPORTED' : backup && (network || disk) ? 'POSSIBLE' : 'INSUFFICIENT_EVIDENCE',
    confidence: network && disk && backup ? 'high' : backup ? 'medium' : 'low',
    supporting: [
      network ? network.summary : null,
      disk ? disk.summary : null,
      backup ? backup.summary : null,
    ].filter((item): item is string => Boolean(item)),
    contradicting: [cpuNormal ? 'CPU remains within its learned expected range.' : null].filter((item): item is string => Boolean(item)),
  });
  hypotheses.push({
    title: 'CPU contention caused the observed activity.',
    status: cpuNormal ? 'DISFAVORED' : cpu ? 'POSSIBLE' : 'INSUFFICIENT_EVIDENCE',
    confidence: cpuNormal ? 'medium' : 'low',
    supporting: cpuNormal ? [] : cpu ? ['CPU telemetry is present but not conclusively normal.'] : [],
    contradicting: cpuNormal ? ['CPU remains within its learned expected range.'] : [],
  });
  const observed = [
    ...active.slice(0, 5).map((item: any) => item.summary),
    ...events.slice(0, 5).map((item: any) => item.summary),
    cpuNormal ? 'CPU remains within its learned expected range.' : null,
  ].filter((item): item is string => Boolean(item));
  return {
    observed,
    hypotheses,
    recommended: [
      'Verify the scheduled backup task and its target.',
      'Check datastore latency during the same evidence window.',
      services.length ? `Check impact to ${services.map((item: any) => item.name).join(', ')}.` : 'Check the affected service and add the missing business-service relationship.',
    ],
    impact: services.length ? services.map((item: any) => `${item.name}: ${item.healthState}`).join(' · ') : 'No modeled service impact.',
  };
}

export function predictiveRisk(forecasts: any[], now = new Date()) {
  const eligible = forecasts.filter((item) =>
    ['high', 'medium'].includes(String(item.quality).toLowerCase()) && Number(item.observationDays ?? 0) >= 7,
  );
  if (!eligible.length)
    return { state: 'UNAVAILABLE' as const, label: 'Prediction unavailable — more history required.', forecast: null };
  const rising = eligible
    .filter((item) => item.estimatedThresholdAt && Date.parse(item.estimatedThresholdAt) > now.getTime())
    .sort((a, b) => Date.parse(a.estimatedThresholdAt) - Date.parse(b.estimatedThresholdAt));
  if (!rising.length)
    return { state: 'CLEAR' as const, label: 'No capacity risk predicted in the next 30 days.', forecast: eligible[0] };
  const forecast = rising[0];
  const days = Math.max(1, Math.ceil((Date.parse(forecast.estimatedThresholdAt) - now.getTime()) / 86_400_000));
  return { state: 'RISK' as const, label: `${customerMetricLabel(String(forecast.metric))} is expected to reach its risk boundary in ${days} days.`, forecast };
}

export function rankAssetOutliers(items: any[], limit = 5) {
  const rank = (field: string) => [...items]
    .filter((item) => Number.isFinite(Number(item[field])))
    .sort((a, b) => Number(b[field]) - Number(a[field]))
    .slice(0, limit);
  return {
    cpu: rank('cpuPercent'),
    memory: rank('memoryPercent'),
    bandwidth: rank('bandwidthBps'),
    temperature: rank('highestTemperatureCelsius'),
    errors: rank('errorRate'),
    anomalous: rank('activeAnomalyCount'),
  };
}

export function aiopsValueMetrics(input: { events?: any[]; findings?: any[]; incidents?: any[] }) {
  const findings = input.findings ?? [];
  return {
    rawEvents: (input.events ?? []).reduce((total, item) => total + Number(item.count ?? 1), 0),
    correlatedFindings: findings.length,
    incidentsCreated: (input.incidents ?? []).length,
    recoveredAutomatically: findings.filter((item) => item.state === 'recovered').length,
  };
}
